import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { alpha, useTheme } from "@mui/material/styles";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useMaterialUIController } from "context";
import { GetQuizResultByDateCall } from "../../redux/api/quizTitle.api";
import {
  getAttemptResultCall,
  listMyAttemptsCall,
} from "../../redux/api/quizSystem.api";

/**
 * Student quiz results — separate pages for mock (practice catalogue)
 * vs university (assignment / join-by-code) attempts.
 *
 * kind: "mock" | "university"
 */
export default function StudentQuizResults({ kind = "mock" }) {
  const isMock = kind === "mock";
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const darkMode = !!controller?.darkMode || theme.palette.mode === "dark";
  const mutedText = darkMode ? theme.palette.ink?.soft || "#94A3B8" : "#64748b";
  const borderColor = darkMode
    ? alpha("#94A3B8", 0.22)
    : "rgba(148,163,184,0.22)";

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [resultDialog, setResultDialog] = useState(false);
  const [resultDetails, setResultDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const title = isMock ? "Mock Results" : "University Results";
  const eyebrow = isMock ? "PRACTICE" : "UNIVERSITY";
  const emptyTitle = isMock
    ? "No mock quiz results yet"
    : "No university quiz results yet";
  const emptyBody = isMock
    ? "Complete a mock quiz from the Mock Quiz page and your scores will show up here."
    : "Join a university quiz with a code or finish an assigned quiz, and your submitted attempts will appear here.";

  const loadMock = useCallback(async () => {
    const res = await GetQuizResultByDateCall();
    const grouped = res?.data && typeof res.data === "object" ? res.data : {};
    const flat = Object.entries(grouped).flatMap(([date, list]) => {
      const arr = Array.isArray(list) ? list : [];
      return arr.map((it) => {
        const row = it?.reservation != null ? it.reservation : it;
        return { ...row, date };
      });
    });
    flat.sort((a, b) => {
      const tA = new Date(a?.created_at || a?.date || 0).getTime();
      const tB = new Date(b?.created_at || b?.date || 0).getTime();
      return tB - tA;
    });
    setRows(flat);
  }, []);

  const loadUniversity = useCallback(async () => {
    const res = await listMyAttemptsCall();
    const data = Array.isArray(res?.data) ? res.data : [];
    const submitted = data
      .filter((a) => a?.status === "submitted")
      .sort((a, b) => {
        const tA = new Date(a?.submitted_at || a?.updated_at || a?.created_at || 0).getTime();
        const tB = new Date(b?.submitted_at || b?.updated_at || b?.created_at || 0).getTime();
        return tB - tA;
      });
    setRows(submitted);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (isMock) await loadMock();
        else await loadUniversity();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isMock, loadMock, loadUniversity]);

  const viewUniversityResult = async (attemptId) => {
    setDetailLoading(true);
    setResultDialog(true);
    try {
      const res = await getAttemptResultCall(attemptId);
      setResultDetails(res?.data || null);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatWhen = (row) => {
    const raw =
      row?.submitted_at || row?.created_at || row?.updated_at || row?.date;
    if (!raw) return "";
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={{ xs: 1, sm: 2 }}>
        <Card
          sx={{
            borderRadius: 4,
            border: `1px solid ${borderColor}`,
            boxShadow: "none",
            overflow: "hidden",
            bgcolor: "background.card",
          }}
        >
          <MDBox
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderBottom: `1px solid ${borderColor}`,
              background: darkMode
                ? `linear-gradient(135deg, ${alpha("#1E3A8A", 0.35)}, ${alpha(
                    "#0F172A",
                    0.2,
                  )})`
                : "linear-gradient(135deg, #EFF6FF, #F8FAFC)",
            }}
          >
            <MDTypography
              sx={{
                fontSize: 11,
                letterSpacing: 1.6,
                fontWeight: 800,
                color: darkMode ? "#A5B4FC" : "#6366f1",
              }}
            >
              {eyebrow}
            </MDTypography>
            <MDTypography
              variant="h4"
              fontWeight="bold"
              sx={{
                color: darkMode ? "#F8FAFC" : "ink.strong",
                letterSpacing: "-0.04em",
                lineHeight: 1.05,
              }}
            >
              {title}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
              {isMock
                ? "Scores from practice mock quizzes."
                : "Submitted scores from university and instructor quizzes."}
            </MDTypography>
          </MDBox>

          <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} />
              </MDBox>
            ) : rows.length === 0 ? (
              <MDBox
                py={6}
                px={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    mb: 1.5,
                    bgcolor: darkMode
                      ? alpha("#60A5FA", 0.12)
                      : alpha("#2563EB", 0.08),
                    color: darkMode ? "#93C5FD" : "#2563eb",
                  }}
                >
                  <AssessmentOutlinedIcon />
                </Box>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: darkMode ? "#F8FAFC" : "ink.strong" }}
                >
                  {emptyTitle}
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{ color: mutedText, mt: 0.75, maxWidth: 420 }}
                >
                  {emptyBody}
                </MDTypography>
              </MDBox>
            ) : (
              rows.map((row, idx) => {
                if (isMock) {
                  const key = row?.resultId || row?.id || `mock-${idx}`;
                  return (
                    <Card
                      key={key}
                      sx={{
                        p: 2,
                        mb: 1.2,
                        borderRadius: 3,
                        border: `1px solid ${borderColor}`,
                        boxShadow: "none",
                      }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                      >
                        <MDTypography variant="button" fontWeight="medium">
                          {row?.title || "Quiz"}
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="bold">
                          {row?.score ?? 0}
                        </MDTypography>
                      </MDBox>
                      {formatWhen(row) ? (
                        <MDTypography variant="caption" sx={{ color: mutedText }}>
                          {formatWhen(row)}
                        </MDTypography>
                      ) : null}
                    </Card>
                  );
                }

                return (
                  <Card
                    key={row.id || `uni-${idx}`}
                    sx={{
                      p: 2,
                      mb: 1.2,
                      borderRadius: 3,
                      border: `1px solid ${borderColor}`,
                      boxShadow: "none",
                    }}
                  >
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Box>
                        <MDTypography variant="button" fontWeight="medium">
                          {row.quizAssignment?.title || "Quiz"} — Score:{" "}
                          {row.score ?? 0}%
                        </MDTypography>
                        {formatWhen(row) ? (
                          <MDTypography
                            variant="caption"
                            sx={{ color: mutedText, display: "block" }}
                          >
                            {formatWhen(row)}
                          </MDTypography>
                        ) : null}
                      </Box>
                      <Button
                        size="small"
                        onClick={() => viewUniversityResult(row.id)}
                      >
                        View result
                      </Button>
                    </MDBox>
                  </Card>
                );
              })
            )}
          </Box>
        </Card>
      </MDBox>

      <Dialog
        open={resultDialog}
        onClose={() => {
          setResultDialog(false);
          setResultDetails(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}
      >
        <DialogTitle>Quiz Result</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <MDBox display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </MDBox>
          ) : resultDetails ? (
            <>
              <MDTypography>
                Score: {resultDetails.score}% — Correct:{" "}
                {resultDetails.correct_count} / {resultDetails.total_questions}
              </MDTypography>
              {(resultDetails.questions || []).map((q, i) => (
                <MDBox
                  key={q.question_bank_id || i}
                  mt={2}
                  p={1.5}
                  sx={{ bgcolor: "background.default", borderRadius: 1 }}
                >
                  <MDTypography
                    variant="caption"
                    color={q.is_correct ? "success" : "error"}
                  >
                    {q.is_correct ? "Correct" : "Incorrect"}
                  </MDTypography>
                  <MDTypography variant="body2" mt={0.5}>
                    {q.question_text || q.content || `Question ${i + 1}`}
                  </MDTypography>
                </MDBox>
              ))}
            </>
          ) : (
            <MDTypography variant="body2" color="text">
              Could not load result details.
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResultDialog(false);
              setResultDetails(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}
